import Phaser from 'phaser';
import { sound } from '../../services/audioService.js';
import { ensureTextures } from '../textureGenerator.js';

export class FunGame extends Phaser.Scene {
  constructor() {
    super({ key: 'FunGame' });
  }

  init(data) {
    this.difficulty = data.difficulty || 'medium';
    this.barsToAdd = data.barsToAdd || (this.difficulty === 'hard' ? 9 : (this.difficulty === 'medium' ? 6 : 3));
    this.onComplete = data.onComplete;
    this.score = 0;
    this.combo = 0;

    if (this.difficulty === 'easy') {
      this.targetScore = 15;
      this.spawnRate = 800;
      this.riseSpeed = 130;
      this.timeLimit = 35;
    } else if (this.difficulty === 'medium') {
      this.targetScore = 25;
      this.spawnRate = 600;
      this.riseSpeed = 180;
      this.timeLimit = 30;
    } else {
      this.targetScore = 35;
      this.spawnRate = 450;
      this.riseSpeed = 240;
      this.timeLimit = 25;
    }
    this.timeLeft = this.timeLimit;
  }

  create() {
    ensureTextures(this);
    const { width, height } = this.cameras.main;
    this.isGameOver = false;

    // Background
    this.add.rectangle(width / 2, height / 2, width, height, 0x141829);

    // Balloons Group
    this.balloons = this.add.group();

    // HUD
    this.scoreText = this.add.text(16, 16, `Pops: ${this.score}/${this.targetScore}`, {
      fontSize: '15px',
      fontStyle: 'bold',
      color: '#FFFFFF'
    });

    this.timerText = this.add.text(width - 16, 16, `⏱️ ${this.timeLeft}s`, {
      fontSize: '15px',
      fontStyle: 'bold',
      color: '#FFD700'
    }).setOrigin(1, 0);

    // Spawn Loop
    this.spawnTimer = this.time.addEvent({
      delay: this.spawnRate,
      callback: this.spawnBalloon,
      callbackScope: this,
      loop: true
    });

    // Countdown Timer
    this.countTimer = this.time.addEvent({
      delay: 1000,
      callback: () => {
        if (this.isGameOver) return;
        this.timeLeft--;
        this.timerText.setText(`⏱️ ${this.timeLeft}s`);
        if (this.timeLeft <= 0) {
          if (this.score >= this.targetScore) {
            this.triggerEnd(true);
          } else {
            this.triggerEnd(false);
          }
        }
      },
      loop: true
    });
  }

  spawnBalloon() {
    if (this.isGameOver) return;
    const { width, height } = this.cameras.main;
    const x = Phaser.Math.Between(40, width - 40);

    const colors = [0xFF6B6B, 0x4D96FF, 0x6BCB77, 0xFFD93D, 0xFF4081, 0x9D4EDD];
    const isGold = Math.random() < 0.15;
    const col = isGold ? 0xFFD700 : Phaser.Utils.Array.GetRandom(colors);

    const container = this.add.container(x, height + 40);

    // Balloon Circle
    const circle = this.add.graphics();
    circle.fillStyle(col, 1);
    circle.fillCircle(0, 0, isGold ? 26 : 22);
    circle.fillTriangle(-6, 20, 6, 20, 0, 26);
    circle.fillStyle(0xFFFFFF, 0.4);
    circle.fillCircle(-8, -8, 6);

    container.add(circle);
    container.setSize(50, 60);
    container.setInteractive({ cursor: 'pointer' });

    container.setData('speed', this.riseSpeed + Phaser.Math.Between(-20, 30));
    container.setData('isGold', isGold);
    container.setData('wobble', Math.random() * Math.PI);

    container.on('pointerdown', () => {
      this.popBalloon(container);
    });

    this.balloons.add(container);
  }

  popBalloon(balloon) {
    if (this.isGameOver || !balloon.active) return;
    const isGold = balloon.getData('isGold');
    const pts = isGold ? 3 : 1;

    sound.playCatch();
    this.score += pts;
    this.scoreText.setText(`Pops: ${this.score}/${this.targetScore}`);

    // Pop animation
    this.tweens.add({
      targets: balloon,
      scaleX: 1.4,
      scaleY: 1.4,
      alpha: 0,
      duration: 100,
      onComplete: () => {
        balloon.destroy();
      }
    });

    if (this.score >= this.targetScore) {
      this.triggerEnd(true);
    }
  }

  update(time, delta) {
    if (this.isGameOver) return;
    const dt = delta / 1000;

    this.balloons.getChildren().forEach((b) => {
      if (b && b.active) {
        b.y -= b.getData('speed') * dt;
        const w = b.getData('wobble') + dt * 3;
        b.setData('wobble', w);
        b.x += Math.sin(w) * 0.8;

        if (b.y < -50) {
          b.destroy();
        }
      }
    });
  }

  triggerEnd(isWin) {
    this.isGameOver = true;
    this.spawnTimer.remove();
    this.countTimer.remove();

    const { width, height } = this.cameras.main;
    this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.75);

    if (isWin) {
      sound.playVictory();
      this.add.text(width / 2, height / 2 - 50, '🎉 HIGH SPIRITS!', {
        fontSize: '24px',
        fontStyle: '800',
        color: '#00E676'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 10, `+${this.barsToAdd} FUN BARS GAINED! 🎮`, {
        fontSize: '15px',
        fontStyle: 'bold',
        color: '#00E676'
      }).setOrigin(0.5);

      const finishBtn = this.add.rectangle(width / 2, height / 2 + 50, 140, 42, 0x00E676)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 50, 'CLAIM & EXIT', {
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#000'
      }).setOrigin(0.5);

      finishBtn.on('pointerdown', () => {
        sound.playClick();
        if (this.onComplete) this.onComplete(true);
      });
    } else {
      sound.playGameOver();
      this.add.text(width / 2, height / 2 - 40, '⏰ TIME IS UP!', {
        fontSize: '24px',
        fontStyle: '800',
        color: '#FF5252'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 5, `Reached ${this.score}/${this.targetScore} pops`, {
        fontSize: '13px',
        color: '#A0A5BA'
      }).setOrigin(0.5);

      const retryBtn = this.add.rectangle(width / 2, height / 2 + 45, 120, 38, 0x4D96FF)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 45, 'RETRY', {
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#FFF'
      }).setOrigin(0.5);

      retryBtn.on('pointerdown', () => {
        sound.playClick();
        this.scene.restart();
      });
    }
  }
}
